import { ref } from 'vue';

import type GuestLoginModal from '@/components/cart/v2/payment/GuestLoginModal.vue';
import type PaymentModal from '@/components/cart/v2/payment/PaymentModal.vue';
import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type { CartDetailsResponse } from '@/types/carts-transactions-api';

import { buildGA4ItemsFromCart, computeCartValue, pushGA4EcommerceEvent } from '@/utils/gtmEcommerce';

export function useCartPaymentFlow(
  cartsTransactionsApi: CartsTransactionsApi,
  cartDetails: { value: CartDetailsResponse | undefined },
) {
  const guestLoginModalRef = ref<InstanceType<typeof GuestLoginModal> | null>(null);
  const paymentModalRef = ref<InstanceType<typeof PaymentModal> | null>(null);
  const isPaymentCompleted = ref(false);
  const completedReferenceNumber = ref<number | null>(null);

  function handlePaymentClick() {
    if (cartDetails.value) {
      pushGA4EcommerceEvent('begin_checkout', {
        currency: cartDetails.value.currency,
        value: computeCartValue(cartDetails.value),
        items: buildGA4ItemsFromCart(cartDetails.value),
      });
    }
    guestLoginModalRef.value?.openModal();
  }

  function handleResumePayment() {
    if (cartDetails.value) {
      paymentModalRef.value?.resumeExistingTransaction();
    }
  }

  function handleCustomerCreated() {
    guestLoginModalRef.value?.closeModal?.();
    paymentModalRef.value?.openModal();
  }

  function handlePaymentCompleted() {
    if (cartDetails.value) {
      pushGA4EcommerceEvent('purchase', {
        transaction_id: String(cartDetails.value.referenceNumber),
        currency: cartDetails.value.currency,
        value: computeCartValue(cartDetails.value),
        items: buildGA4ItemsFromCart(cartDetails.value),
      });
    }

    completedReferenceNumber.value = cartDetails.value?.referenceNumber ?? null;
    cartsTransactionsApi.clearSession();
    cartsTransactionsApi.clearStoredData();
    cartDetails.value = undefined;
    isPaymentCompleted.value = true;
  }

  function markAsCompleted(referenceNumber: number | null) {
    completedReferenceNumber.value = referenceNumber;
    isPaymentCompleted.value = true;
  }

  return {
    guestLoginModalRef,
    paymentModalRef,
    isPaymentCompleted,
    completedReferenceNumber,
    handlePaymentClick,
    handleResumePayment,
    handleCustomerCreated,
    handlePaymentCompleted,
    markAsCompleted,
  };
}
